import { StyleSheet, Text, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import type { ClosingTimeRequestAction } from "@luna-restaurant/school-api";
import { Button, Icon, useCustomTheme } from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import { ChefHatModal } from "@/components/modals/ChefHatModal";

type ConfirmDeleteClosingTimeRequestModalProps = {
  visible: boolean;
  requestAction?: ClosingTimeRequestAction;
  onDismiss: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
};

const modalContentByAction: Record<
  ClosingTimeRequestAction,
  {
    headerKey: string;
    titleKey: string;
    descriptionKey: string;
    submitButtonTextKey: string;
  }
> = {
  CREATE: {
    headerKey: "closeTimes.deleteRequestModal.create.header",
    titleKey: "closeTimes.deleteRequestModal.create.title",
    descriptionKey: "closeTimes.deleteRequestModal.create.description",
    submitButtonTextKey:
      "closeTimes.deleteRequestModal.create.submitButtonText",
  },
  UPDATE: {
    headerKey: "closeTimes.deleteRequestModal.update.header",
    titleKey: "closeTimes.deleteRequestModal.update.title",
    descriptionKey: "closeTimes.deleteRequestModal.update.description",
    submitButtonTextKey:
      "closeTimes.deleteRequestModal.update.submitButtonText",
  },
  DELETE: {
    headerKey: "closeTimes.deleteRequestModal.delete.header",
    titleKey: "closeTimes.deleteRequestModal.delete.title",
    descriptionKey: "closeTimes.deleteRequestModal.delete.description",
    submitButtonTextKey:
      "closeTimes.deleteRequestModal.delete.submitButtonText",
  },
};

export function ConfirmDeleteClosingTimeRequestModal({
  visible,
  requestAction = "CREATE",
  onDismiss,
  onConfirm,
  isSubmitting = false,
}: ConfirmDeleteClosingTimeRequestModalProps) {
  const { colors, fonts } = useCustomTheme();
  const { t } = useTranslation();
  const modalContent = modalContentByAction[requestAction];

  return (
    <ChefHatModal
      visible={visible}
      icon={{ icon: "restore", iconSet: "material-community" }}
      iconSize={50}
      onDismiss={onDismiss}
    >
      <View style={styles.header}>
        <Icon
          iconSet="font-awesome"
          icon="bell-exclamation"
          style="solid"
          size={20}
          color={colors.danger}
        />
        <Text style={[styles.headerText, fonts.bold, { color: colors.danger }]}>
          {t(modalContent.headerKey)}
        </Text>
      </View>

      <Text style={[styles.title, fonts.bold, { color: colors.text }]}>
        {t(modalContent.titleKey)}
      </Text>
      <Text style={[styles.description, fonts.regular, { color: colors.text }]}>
        {t(modalContent.descriptionKey)}
      </Text>

      <View style={styles.actions}>
        <SecondaryButton
          label={t("closeTimes.deleteRequestModal.cancelButtonText")}
          onPress={onDismiss}
          disabled={isSubmitting}
          iconName="close"
          iconPosition="left"
        />

        <Button
          buttonText={t(modalContent.submitButtonTextKey)}
          onPress={onConfirm}
          disabled={isSubmitting}
          buttonStyle={[
            styles.primaryButton,
            { backgroundColor: colors.border },
          ]}
          buttonTextStyle={[
            styles.primaryButtonText,
            fonts.bold,
            { color: colors.background },
          ]}
          iconName="arrow-right"
          iconColor={colors.background}
          iconPosition="right"
          iconSize={24}
        />
      </View>
    </ChefHatModal>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 24,
  },
  headerText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  title: {
    fontSize: 32,
    lineHeight: 40,
    marginBottom: 24,
  },
  description: {
    fontSize: 20,
    lineHeight: 25,
    marginBottom: 32,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  primaryButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 24,
  },
  primaryButtonText: {
    fontSize: 16,
  },
});
